import currencyUtil from '@/modules/common/utils/currencyUtil';

const DEFAULT_PAYMENT_TYPE = 'monthly';
const SUBSCRIPTION_REQUIRED_PAYMENT_TYPES = ['external', 'single_charge'];

const findUserPrice = (geoData, offering) => {
  if (!geoData) return undefined;

  const accountCurrency = currencyUtil.getCurrency(geoData.country, geoData.continent);
  return offering.prices?.find((item) => item.currency === accountCurrency);
};

const isSubscriptionRequiredOffering = (offering) => SUBSCRIPTION_REQUIRED_PAYMENT_TYPES.includes(offering?.payment_type);
const isBundleOffering = (offering) => offering?.payment_type === 'bundle';

const pickUsableOffering = (offeringsForSku, subscription) => {
  if (!offeringsForSku?.length) return undefined;

  if (subscription?.payment_type) {
    const subscriptionMatch = offeringsForSku.find((offering) => offering.payment_type === subscription.payment_type);
    if (subscriptionMatch) return subscriptionMatch;
  }

  return offeringsForSku.find((offering) => !isSubscriptionRequiredOffering(offering) && !isBundleOffering(offering))
    || offeringsForSku.find((offering) => !isSubscriptionRequiredOffering(offering));
};

const groupOfferingsBySku = (offerings) => offerings.reduce((acc, offering) => {
  if (!offering.SKU) return acc;
  if (!acc[offering.SKU]) acc[offering.SKU] = [];
  acc[offering.SKU].push(offering);
  return acc;
}, {});

const purchasedAppData = (subscription, priceInfo = null, offering = null) => {
  const paymentType = subscription.payment_type || DEFAULT_PAYMENT_TYPE;

  return {
    base_price: priceInfo?.price ?? subscription.purchase_price,
    subscription_uid: subscription.uid,
    price: subscription.purchase_price,
    currency: subscription.purchase_currency,
    payment_type: paymentType,
    purchase_state: subscription.purchase_state,
    subscription_created_at: subscription.created_at,
    price_uid: priceInfo?.price_uid || priceInfo?.uid,
    pay_per_unit: priceInfo?.pay_per_unit || [],
    requires_purchase: false,
    is_free: paymentType === 'free',
    is_included: paymentType === 'bundle',
    is_external: paymentType === 'external',
    offering_uid: offering?.uid || null,
    vendor: offering?.vendor || null,
  };
};

const paidAppData = (priceInfo, offering) => {
  const paymentType = offering?.payment_type;
  const isFree = paymentType === 'free';
  const isBundled = paymentType === 'bundle';
  const isExternal = paymentType === 'external';

  return {
    base_price: priceInfo.price,
    price: priceInfo.price,
    currency: priceInfo.currency,
    payment_type: paymentType,
    purchase_state: 'not_purchased',
    price_uid: priceInfo.price_uid || priceInfo.uid,
    pay_per_unit: priceInfo.pay_per_unit || [],
    requires_purchase: !isFree && !isBundled && !isExternal,
    is_free: isFree,
    is_included: isBundled,
    is_external: isExternal,
    offering_uid: offering?.uid || null,
    vendor: offering?.vendor || null,
  };
};

const isMarketCandidate = (app) => app.has_offering || app.status === 'installed';

const aggregateMarketApps = ({
  apps, offerings, subscriptions, geoData,
}) => {
  const offeringsBySku = groupOfferingsBySku(offerings);

  return apps.filter(isMarketCandidate).reduce((result, app) => {
    const subscription = subscriptions.find((sub) => sub.sku === app.app_code_name);
    const offering = pickUsableOffering(offeringsBySku[app.app_code_name], subscription);
    const isInstalled = app.status === 'installed';

    if (!offering) {
      result.push(app);
      return result;
    }

    const enrichedApp = {
      ...app,
      is_listed: Boolean(subscription) || offering.is_listed === true,
    };
    const priceInfo = findUserPrice(geoData, offering);

    if (!priceInfo) {
      if (!isInstalled) return result;

      enrichedApp.uninstall_only = true;
      if (subscription) enrichedApp.paid_data = purchasedAppData(subscription);
    } else if (subscription) {
      enrichedApp.paid_data = purchasedAppData(subscription, priceInfo, offering);
      enrichedApp.requires_upgrade = app.requires_upgrade || app.requires_upgrade_if_paid;
    } else if (isBundleOffering(offering)) {
      if (!isInstalled) return result;

      enrichedApp.uninstall_only = true;
    } else {
      enrichedApp.paid_data = paidAppData(priceInfo, offering);
      enrichedApp.requires_upgrade = app.requires_upgrade || app.requires_upgrade_if_paid;
    }

    result.push(enrichedApp);
    return result;
  }, []);
};

export default {
  aggregateMarketApps,
  findUserPrice,
  pickUsableOffering,
  groupOfferingsBySku,
  purchasedAppData,
  paidAppData,
};
